/**
 * Frontend Logger Utility
 *
 * Provides structured logging with environment-aware behavior:
 * - Development: All log levels active
 * - Production: Only warn() and error() are active
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  module?: string;
}

/**
 * Sanitize error objects for safe logging
 * Removes stack traces in production
 */
function sanitizeError(error: unknown): string {
  if (error instanceof Error) {
    const isDev = import.meta.env.DEV;
    if (isDev) {
      return `${error.name}: ${error.message}\n${error.stack || ''}`;
    }
    return `${error.name}: ${error.message}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Format log message with optional module prefix
 */
function formatMessage(message: string, options?: LoggerOptions): string {
  if (options?.module) {
    return `[${options.module}] ${message}`;
  }
  return message;
}

/**
 * Core logging function
 */
function log(level: LogLevel, message: string, data?: unknown, options?: LoggerOptions): void {
  const isDev = import.meta.env.DEV;

  // In production, only allow warn and error
  if (!isDev && (level === 'debug' || level === 'info')) {
    return;
  }

  const formattedMessage = formatMessage(message, options);

  if (data !== undefined) {
    // Handle error objects specially
    if (data instanceof Error) {
      const sanitized = sanitizeError(data);
      console[level](formattedMessage, sanitized);
    } else {
      console[level](formattedMessage, data);
    }
  } else {
    console[level](formattedMessage);
  }
}

/**
 * Logger instance with all log level methods
 */
export const logger = {
  /**
   * Debug-level logging (development only)
   * Use for detailed diagnostic information
   */
  debug: (message: string, data?: unknown, options?: LoggerOptions) => {
    log('debug', message, data, options);
  },

  /**
   * Info-level logging (development only)
   * Use for general informational messages
   */
  info: (message: string, data?: unknown, options?: LoggerOptions) => {
    log('info', message, data, options);
  },

  /**
   * Warning-level logging (all environments)
   * Use for potentially harmful situations
   */
  warn: (message: string, data?: unknown, options?: LoggerOptions) => {
    log('warn', message, data, options);
  },

  /**
   * Error-level logging (all environments)
   * Use for error events
   */
  error: (message: string, data?: unknown, options?: LoggerOptions) => {
    log('error', message, data, options);
  },
};

/**
 * Create a logger instance with a module name prefix
 *
 * @example
 * const log = createLogger('MyComponent');
 * log.debug('Component mounted');
 */
export function createLogger(module: string) {
  return {
    debug: (message: string, data?: unknown) => logger.debug(message, data, { module }),
    info: (message: string, data?: unknown) => logger.info(message, data, { module }),
    warn: (message: string, data?: unknown) => logger.warn(message, data, { module }),
    error: (message: string, data?: unknown) => logger.error(message, data, { module }),
  };
}
